import { useState } from 'react';
import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { TagsUtils } from '@frontend/api-tag';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  Checkbox,
  Chip,
  PopoverMenuItem,
  PrimaryButton,
  SearchField,
  Text,
  TextButton,
  UsePopoverMenuResponse,
  useDebouncedValue,
  useFormField,
} from '@frontend/design-system';
import { useInboxFiltersShallowStore } from '../../stores/inbox-filters-store';

type SmsTagsFilterPopoverContentProps = {
  tags: Tag[];
  popover: UsePopoverMenuResponse<HTMLElement, false, HTMLMenuElement, HTMLButtonElement>;
  goBack: () => void;
  applyTags: (tagIds: string[]) => void;
  hasStoreClear?: boolean;
};

export const TagsBulkSelectPopover = ({
  tags,
  popover,
  goBack,
  applyTags,
  hasStoreClear,
}: SmsTagsFilterPopoverContentProps) => {
  const { t } = useTranslation('fax');
  const searchFieldProps = useFormField({ type: 'text' });
  const { inboxFilters, setInboxFilters } = useInboxFiltersShallowStore('inboxFilters', 'setInboxFilters');

  const [selectedTags, setSelectedTags] = useState<string[]>(inboxFilters.tags ?? []);

  const handleClear = () => {
    setSelectedTags([]);
    if (hasStoreClear) setInboxFilters({ ...inboxFilters, tags: [] });
  };

  const handleTagToggle = (id: string) => {
    if (selectedTags.includes(id)) {
      setSelectedTags((prev) => prev.filter((tagId) => tagId !== id));
    } else {
      setSelectedTags((prev) => [...prev, id]);
    }
  };

  const debouncedSearchValue = useDebouncedValue(searchFieldProps.value, 150);
  const filteredTags = tags.filter((tag) => tag.name.toLowerCase().includes(debouncedSearchValue.toLowerCase()));
  const filteredTagsIds = filteredTags.map((tag) => tag.id || tag.smartTagId);

  const isDisabled = !hasStoreClear && selectedTags.length === 0;

  return (
    <>
      <PopoverMenuItem {...popover} css={{ cursor: 'pointer' }} onClick={goBack}>
        <Icon name='caret-left' size={16} />
        <Text>{t('Tags')}</Text>
      </PopoverMenuItem>
      <div css={{ padding: theme.spacing(0.5, 1.5) }}>
        <SearchField
          {...searchFieldProps}
          name='tag-search'
          css={{ maxWidth: 200 }}
          onKeyDownCapture={(e) => {
            e.stopPropagation();
          }}
        />
      </div>
      <PopoverMenuItem
        as='span'
        {...popover}
        css={{
          padding: theme.spacing(0.5),
          justifyContent: 'space-between',
          ':hover': {
            backgroundColor: 'inherit',
          },
          ':focus': {
            backgroundColor: 'inherit',
          },
        }}
      >
        <TextButton
          css={{ fontWeight: 'bold', color: theme.colors.primary50 }}
          onClick={() => setSelectedTags(filteredTagsIds)}
        >
          {t('Select all')}
        </TextButton>
        {!isDisabled && (
          <TextButton
            css={{
              fontWeight: 'bold',
              color: theme.colors.primary50,
            }}
            onClick={handleClear}
          >
            {t('Clear')}
          </TextButton>
        )}
      </PopoverMenuItem>
      <div css={{ overflowY: 'auto', overflowX: 'hidden', maxHeight: '60vh' }}>
        {filteredTags.map((tag, i) => {
          const tagId = tag.id || tag.smartTagId;
          return (
            <PopoverMenuItem
              key={tagId}
              as='span'
              {...popover.getItemProps({
                index: i + 2,
                disableCloseOnSelect: true,
                onClick: () => {
                  handleTagToggle(tagId);
                },
              })}
              css={{
                width: '100%',
                height: 'auto',
                padding: theme.spacing(1),
                cursor: 'pointer',
                svg: { fill: theme.colors.white },
              }}
            >
              <Checkbox
                id={tagId}
                value={!!selectedTags.includes(tagId)}
                onChange={() => {}}
                onFocus={() => {}}
                onBlur={() => {}}
                aria-invalid={false}
                active={false}
                error={false}
                name={tagId}
              />

              <Chip.Tag color={TagsUtils.convertStringToTagColor(tag.color)} css={{ cursor: 'pointer' }}>
                {tag.name}
              </Chip.Tag>
            </PopoverMenuItem>
          );
        })}
      </div>
      <PopoverMenuItem
        as='span'
        onClick={() => applyTags(selectedTags)}
        css={[{ padding: theme.spacing(2), marginTop: theme.spacing(1) }]}
        {...popover}
      >
        <PrimaryButton disabled={isDisabled}>{t('Apply')}</PrimaryButton>
      </PopoverMenuItem>
    </>
  );
};
